// OrganizationCampaignManagement.js (or add to AdminDashboardPage.js)
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { CheckCircleIcon, XCircleIcon, ShieldExclamationIcon, InboxIcon } from '@heroicons/react/24/solid'; // Import the icons

// --- Sub-component for Organization Campaign Card ---
const OrganizationCampaignCard = ({ campaign, onUpdate }) => {
  const [notes, setNotes] = useState('');
  const handleApprove = () => {
    onUpdate(campaign.id, 'approved', 'Campaign approved via admin panel.');
  };

  const handleReject = () => {
    if (!notes.trim()) {
      alert('A reason is required to reject a campaign.');
      return;
    }
    onUpdate(campaign.id, 'rejected', notes);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
      <h3 className="font-bold text-lg text-gray-800 dark:text-white">{campaign.product_name}</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400">Organization: {campaign.organization_name}</p>

      <div className="mt-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Rejection Notes (if applicable):</label>
        <textarea
          className="mt-1 w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
          rows="2"
          placeholder="Provide a clear reason for rejection..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <div className="flex gap-4 mt-4">
        <button
          onClick={handleApprove}
          className="flex-1 flex items-center justify-center py-2 px-4 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors"
        >
          <CheckCircleIcon className="w-5 h-5 mr-2" />
          Approve
        </button>
        <button
          onClick={handleReject}
          className="flex-1 flex items-center justify-center py-2 px-4 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors"
        >
          <XCircleIcon className="w-5 h-5 mr-2" />
          Reject
        </button>
      </div>
    </div>
  );
};

// --- Main Admin Dashboard Page Component ---
export default function OrganizationCampaignManagement() {
    const { token } = useAuth();
    const [pending, setPending] = useState([]);
    const [approved, setApproved] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

   // Inside the fetchData function
const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
        const [pendingRes, approvedRes] = await Promise.all([
            fetch(`${process.env.REACT_APP_API_URL}/admin/organization/campaigns/pending`, { headers: { 'Authorization': `Bearer ${token}` }}),
            fetch(`${process.env.REACT_APP_API_URL}/admin/organization/campaigns/approved`, { headers: { 'Authorization': `Bearer ${token}` }})
        ]);

        if (!pendingRes.ok || !approvedRes.ok) throw new Error('Failed to fetch admin data.');

        const pendingData = await pendingRes.json();
        const approvedData = await approvedRes.json();

        setPending(pendingData);
        setApproved(approvedData);
        console.log("Pending Campaigns:", pendingData);  // <--- ADD THIS
        console.log("Approved Campaigns:", approvedData); // <--- ADD THIS
    } catch (err) {
        setError(err.message);
    } finally {
        setLoading(false);
    }
}, [token]);
    useEffect(() => { fetchData(); }, [fetchData]);

    const handleUpdate = async (id, status, notes) => {
        await fetch(`${process.env.REACT_APP_API_URL}/admin/campaigns/${id}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ status, adminNotes: notes })
        });
        fetchData();
    };

    if (loading) return <div className="text-center text-xl p-8 text-gray-600 dark:text-gray-300">Loading Organization Campaigns...</div>;
    if (error) return <p className="text-center text-xl p-8 text-red-500">{error}</p>;

    return (
        <div className="space-y-10">
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">Organization Campaign Management</h1>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                <section>
                    <div className="flex items-center mb-4">
                        <ShieldExclamationIcon className="w-8 h-8 text-yellow-500 mr-3" />
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Pending ({pending.length})</h2>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-xl space-y-4">
                        {pending.length > 0 ? (
                            pending.map(campaign => <OrganizationCampaignCard key={campaign.id} campaign={campaign} onUpdate={handleUpdate} />)
                        ) : ( <div className="text-center py-8"><InboxIcon className="w-16 h-16 mx-auto text-gray-400" /><p className="mt-4 text-lg text-gray-500">The approval queue is empty.</p></div> )}
                    </div>
                </section>

                <section>
                    <div className="flex items-center mb-4">
                        <CheckCircleIcon className="w-8 h-8 text-green-500 mr-3" />
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Approved ({approved.length})</h2>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-xl space-y-4">
                        {approved.length > 0 ? (
                            approved.map(campaign => (
                                <div key={campaign.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
                                    <h3 className="font-bold text-lg text-gray-800 dark:text-white">{campaign.product_name}</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Organization: {campaign.organization_name}</p>
                                </div>
                            ))
                        ) : ( <div className="text-center py-8"><InboxIcon className="w-16 h-16 mx-auto text-gray-400" /><p className="mt-4 text-lg text-gray-500">No approved campaigns.</p></div> )}
                    </div>
                </section>
            </div>
        </div>
    );
}